import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ChevronDown } from 'lucide-react';
import { ReviewCard } from './ReviewCard';
import { RatingSummary } from './RatingSummary';
import { Manifesto, Marquee, ReviewCta } from './ReviewBits';
import { PRODUCT_NAMES } from './reviewUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE = 9;

const FILTERS = [
  { key: 'all', label: 'All paddles', testId: 'reviews-filter-tab-all' },
  { key: 'hexcore', label: 'HexCore Pro', testId: 'reviews-filter-tab-hexcore' },
  { key: 'profoam', label: 'ProFoam Pro', testId: 'reviews-filter-tab-profoam' },
  { key: 'verified', label: 'Order verified', testId: 'reviews-filter-tab-verified' },
];

const EmptyState = ({ count }) => (
  <div className="sm:col-span-2 md:col-span-3 rounded-2xl border border-dashed border-[#111214]/25 p-8 sm:p-10 bg-white/60" data-testid="reviews-empty-state">
    <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#E02128]">Early days</p>
    <h3 className="font-display text-3xl sm:text-4xl text-[#111214] mt-2 leading-none">
      {count === 0 ? 'No reviews yet. And we won\'t fake any.' : `${count} player${count === 1 ? ' has' : 's have'} spoken so far.`}
    </h3>
    <p className="text-[#52525B] mt-3 max-w-xl text-sm sm:text-base">
      {count === 0
        ? 'The first honest words from a real DYSSC player will appear right here, unedited.'
        : 'Every word here is theirs, as written. More arrive after every delivery.'}
    </p>
  </div>
);

export const HomeReviews = () => {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('all');
  const [shown, setShown] = useState(PAGE);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/reviews/featured`).then(({ data: d }) => setData(d)).catch(() => setData({ reviews: [], aggregate: { avg: 0, count: 0 } }));
  }, []);

  const list = useMemo(() => {
    const all = data?.reviews || [];
    if (filter === 'verified') return all.filter((r) => r.verified);
    if (filter === 'all') return all;
    return all.filter((r) => r.product_slug === filter);
  }, [data, filter]);

  const agg = data?.aggregate || { avg: 0, count: 0 };
  const visible = list.slice(0, shown);
  const featuredIdx = visible.findIndex((r) => (r.body || '').trim().length > 60);

  return (
    <section id="reviews" className="relative bg-[#F6F6F4] pb-20 sm:pb-28 overflow-hidden" data-testid="reviews-section" aria-labelledby="reviews-heading">
      {data && <Marquee reviews={data.reviews} />}
      <span className="pointer-events-none select-none absolute top-14 right-[-2%] font-display text-[22vw] leading-none text-[#111214]/[0.035] tracking-tight" aria-hidden="true">HONEST</span>
      <div className="relative max-w-7xl mx-auto px-6 pt-16 sm:pt-20">
        <div className="grid lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-16 items-end">
          <div>
            <p className="font-mono text-[11px] sm:text-xs tracking-[0.22em] uppercase text-[#E02128]">Honest &amp; true · from real players</p>
            <h2 id="reviews-heading" className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.9] text-[#111214] mt-4">
              Real players.<br /><span className="text-[#E02128]">Real reviews.</span>
            </h2>
            <p className="mt-6 text-[#52525B] text-base sm:text-lg max-w-xl leading-relaxed">
              What Indian players actually say after weeks on court with a DYSSC paddle — matched to real orders and published as written.
            </p>
          </div>
          <RatingSummary aggregate={agg} />
        </div>

        <div className="mt-14"><Manifesto /></div>

        <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-controls="reviews-detail"
          className="mt-10 w-full flex items-center justify-between gap-4 rounded-2xl border border-dashed border-[#111214]/30 bg-white/70 hover:bg-white hover:border-[#111214] px-5 sm:px-7 py-5 text-left transition-colors group"
          data-testid="reviews-toggle">
          <span>
            <span className="block font-heading font-bold text-[#111214] text-lg sm:text-xl">
              {open ? 'Collapse the reviews' : 'Click here to expand and read the reviews'}
            </span>
            <span className="block text-sm text-[#71717A] mt-0.5">{agg.count} player review{agg.count === 1 ? '' : 's'} · filter by paddle or order-verified</span>
          </span>
          <span className="shrink-0 w-10 h-10 rounded-full bg-[#111214] text-white flex items-center justify-center transition-transform group-hover:scale-105">
            <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
          </span>
        </button>

        <div id="reviews-detail" className={open ? '' : 'hidden'} data-testid="reviews-detail">
        <div className="mt-8 flex flex-wrap items-center gap-2" role="tablist" aria-label="Filter reviews">
          {FILTERS.map((f) => {
            const n = f.key === 'all' ? agg.count : f.key === 'verified' ? agg.verified : (data?.products || []).find((p) => p.slug === f.key)?.count;
            return (
              <button key={f.key} role="tab" aria-selected={filter === f.key} onClick={() => { setFilter(f.key); setShown(PAGE); }}
                className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${filter === f.key ? 'bg-[#111214] text-white border-[#111214]' : 'bg-white text-[#52525B] border-[#E4E4E7] hover:border-[#111214]/40'}`}
                data-testid={f.testId}>
                {f.label}{typeof n === 'number' ? <span className={`ml-1.5 font-mono text-[11px] ${filter === f.key ? 'text-zinc-400' : 'text-[#A1A1AA]'}`}>{n}</span> : null}
              </button>
            );
          })}
        </div>

        <div className="mt-6 grid sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-5" data-testid="reviews-grid">
          {visible.map((r, i) => <ReviewCard key={`${r.name}-${r.created_at}-${i}`} review={r} index={i} featured={i === featuredIdx} />)}
          {data && list.length < 3 && <EmptyState count={list.length} />}
        </div>

        {list.length > shown && (
          <div className="mt-8 flex justify-center">
            <button onClick={() => setShown((s) => s + PAGE)} className="px-6 py-3 rounded-full border border-[#111214] text-[#111214] text-sm font-semibold hover:bg-[#111214] hover:text-white transition-colors" data-testid="reviews-show-more">
              Show more reviews ({list.length - shown})
            </button>
          </div>
        )}
        </div>
      </div>
    </section>
  );
};

export const ProductReviews = ({ slug, reviews = [], aggregate }) => {
  const agg = aggregate || { avg: 0, count: 0 };
  const name = PRODUCT_NAMES[slug] || 'DYSSC paddle';
  return (
    <section id="reviews" className="mt-10 max-w-3xl scroll-mt-24" data-testid="product-reviews" aria-labelledby="product-reviews-heading">
      <p className="font-mono text-[11px] tracking-[0.22em] uppercase text-[#E02128]">Honest &amp; true · from real players</p>
      <h2 id="product-reviews-heading" className="font-display text-2xl md:text-3xl mt-2 mb-6 text-[#111214]">Player reviews of the {name}</h2>
      <RatingSummary aggregate={agg} />
      <div className="mt-6"><Manifesto compact /></div>
      <div className="mt-6 grid gap-4" data-testid="product-reviews-list">
        {reviews.map((r, i) => <ReviewCard key={i} review={{ ...r, product_slug: r.product_slug || slug }} index={i} />)}
        {reviews.length === 0 && (
          <div className="rounded-2xl border border-dashed border-[#111214]/25 p-6 bg-white/60" data-testid="reviews-empty-state">
            <h3 className="font-display text-2xl text-[#111214] leading-none">No reviews yet. And we won't fake any.</h3>
            <p className="text-[#52525B] mt-2 text-sm">The first honest words from a real {name} player will appear here, unedited.</p>
          </div>
        )}
      </div>
      <div className="mt-8"><ReviewCta product={name} /></div>
    </section>
  );
};
